<template>
  <v-card>
    <v-card-title class="d-flex align-center justify-space-between">
      <div>
        <v-icon left>mdi-chart-bar</v-icon>
        Quotes by Occasion
      </div>
    </v-card-title>
    <v-card-text>
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      <div v-else-if="!occasionData || occasionData.length === 0" class="text-center py-8 text-grey">
        No occasion data available
      </div>
      <div v-else>
        <apexchart
          type="bar"
          height="400"
          :options="chartOptions"
          :series="series"
        />
        
        <!-- Occasion details table -->
        <v-card class="mt-6" elevation="2">
          <v-card-title>
            <v-icon left>mdi-table</v-icon>
            Occasion Breakdown
          </v-card-title>
          <v-card-text>
            <v-table>
              <thead>
                <tr>
                  <th>Occasion</th>
                  <th>Count</th>
                  <th>Percentage</th>
                  <th>Visual</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="occasion in occasionData" :key="occasion.occasion">
                  <td>
                    <div class="d-flex align-center">
                      <v-icon class="me-2" :color="getOccasionColor(occasion.occasion)">
                        {{ getOccasionIcon(occasion.occasion) }}
                      </v-icon>
                      {{ getOccasionLabel(occasion.occasion) }}
                    </div>
                  </td>
                  <td class="font-weight-bold">{{ occasion.count }}</td>
                  <td class="font-weight-bold">{{ occasion.percentage }}%</td>
                  <td>
                    <v-progress-linear
                      :model-value="parseFloat(occasion.percentage)"
                      :color="getOccasionColor(occasion.occasion)"
                      height="8"
                      rounded
                    ></v-progress-linear>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import { statisticsService } from "../services";
import { OCCASIONS } from "../constants/occasions";
import { Occasion, getOccasionLabel } from "../constants/occasions";

export default {
  name: "OccasionBarChart",
  components: { apexchart: VueApexCharts },
  props: {
    startDate: { type: [String, Date], required: true },
    endDate: { type: [String, Date], required: true },
  },
  data() {
    return {
      loading: true,
      occasionData: [],
      totalQuotes: 0,
      chartOptions: {
        chart: {
          id: "occasion-bar-chart",
          type: "bar",
          toolbar: { show: false },
          animations: { enabled: true }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "70%",
            borderRadius: 4
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val;
          }
        },
        xaxis: {
          type: 'category',
          labels: {
            rotate: -45,
            style: {
              fontSize: "12px"
            }
          }
        },
        yaxis: {
          title: {
            text: "Number of Quotes"
          }
        },
        tooltip: {
          y: {
            formatter: function (val, { seriesIndex, dataPointIndex, w }) {
              const count = val;
              const dataPoint = w.config.series[0].data[dataPointIndex];
              const percentage = dataPoint && dataPoint.percentage ? dataPoint.percentage : 0;
              return `${count} quotes (${percentage}%)`;
            }
          }
        },
        legend: {
          position: "top"
        }
      }
    };
  },
  computed: {
    series() {
      if (!this.occasionData.length) return [];
      
      return [
        {
          name: "Count",
          data: this.occasionData.map(item => ({
            x: this.getOccasionLabel(item.occasion),
            y: item.count,
            fillColor: this.getOccasionColor(item.occasion),
            percentage: item.percentage
          }))
        },
      ];
    }
  },
  watch: {
    startDate: { immediate: true, handler() { this.loadOccasionData(); } },
    endDate: { immediate: true, handler() { this.loadOccasionData(); } },
  },
  methods: {
    async loadOccasionData() {
      this.loading = true;
      try {
        const startYmd = this.toYmd(this.startDate);
        const endYmd = this.toYmd(this.endDate);
        const response = await statisticsService.getQuotesByOccasion({
          startIso: startYmd,
          endIso: endYmd
        });
        if (response.success) {
          this.occasionData = response.data.occasions;
          this.totalQuotes = response.data.totalQuotes;
        } else {
          this.occasionData = [];
        }
      } catch (error) {
        console.error("Error loading occasion data:", error);
        this.occasionData = [];
      } finally {
        this.loading = false;
      }
    },
    toYmd(d) {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    
    
    getOccasionLabel(occasion) {
      return OCCASIONS[occasion]?.label || occasion;
    },
    
    getOccasionIcon(occasion) {
      const iconMap = {
        'Birthday': "mdi-cake",
        'Wedding': "mdi-heart",
        'Anniversary': "mdi-ring",
        'Corporate Event': "mdi-briefcase",
        'Baby Shower': "mdi-baby-face",
        'Holiday Party': "mdi-party-popper",
        'School Event': "mdi-school",
        'Back Yard Party': "mdi-home",
        'Celebration of Life': "mdi-flower",
        'Retirement Party': "mdi-account-tie",
        'Rehearsal Dinner': "mdi-silverware-fork-knife",
        'Other': "mdi-calendar"
      };
      return iconMap[occasion] || "mdi-calendar";
    },
    
    getOccasionColor(occasion) {
      const colorMap = {
        'Birthday': "#FF6B6B",
        'Wedding': "#4ECDC4",
        'Anniversary': "#45B7D1",
        'Corporate Event': "#96CEB4",
        'Baby Shower': "#FFEAA7",
        'Holiday Party': "#DDA0DD",
        'School Event': "#98D8C8",
        'Back Yard Party': "#F7DC6F",
        'Celebration of Life': "#BB8FCE",
        'Retirement Party': "#85C1E9",
        'Rehearsal Dinner': "#F8C471",
        'Other': "#95A5A6"
      };
      return colorMap[occasion] || "#95A5A6";
    }
  },
  mounted() {
    this.loadOccasionData();
  }
};
</script>

<style scoped>
.py-8 {
  padding-top: 32px;
  padding-bottom: 32px;
}
</style>
